import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Briefcase, FileText, LayoutDashboard, LogOut, MessageSquare, Scale, Settings, ShieldCheck, User, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Avatar } from "@/components/common";
import { cn } from "@/lib/utils";

const NAV = {
  lawyer: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
    { to: "/cases", label: "Cases", icon: Briefcase, id: "cases" },
    { to: "/clients", label: "Clients", icon: Users, id: "clients" },
    { to: "/messages", label: "Messages", icon: MessageSquare, id: "messages" },
    { to: "/staff", label: "Staff", icon: ShieldCheck, id: "staff" },
    { to: "/settings", label: "Settings", icon: Settings, id: "settings" },
  ],
  client: [
    { to: "/cases", label: "My Cases", icon: Briefcase, id: "cases" },
    { to: "/messages", label: "Messages", icon: MessageSquare, id: "messages" },
    { to: "/documents", label: "Documents", icon: FileText, id: "documents" },
    { to: "/profile", label: "Profile", icon: User, id: "profile" },
  ],
};
NAV.staff = NAV.lawyer.filter((n) => n.id !== "staff");

const ROLE_LABEL = { lawyer: "Lawyer", staff: "Staff Access", client: "Client" };

const Brand = ({ dark }) => (
  <div className="flex items-center gap-2.5">
    <div className="grid h-9 w-9 place-items-center rounded-xl bg-gold text-white shadow-gold"><Scale size={18} /></div>
    <div className={cn("font-heading text-[15px] font-bold leading-tight", dark ? "text-white" : "text-navy")}>
      My Lawyer<br /><span className="text-gold">My Client</span>
    </div>
  </div>
);

const RoleBadge = ({ type }) => (
  <span data-testid="role-badge" className={cn("rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider",
    type === "staff" ? "bg-amber-100 text-amber-800" : type === "lawyer" ? "bg-navy text-white" : "bg-emerald-100 text-emerald-800")}>
    {ROLE_LABEL[type]}
  </span>
);

const Sidebar = ({ items, user, onLogout }) => (
  <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col bg-navy p-5 md:flex">
    <Brand dark />
    <nav className="mt-10 flex flex-1 flex-col gap-1">
      {items.map(({ to, label, icon: Icon, id }) => (
        <NavLink key={id} to={to} data-testid={`nav-${id}-tab`}
          className={({ isActive }) => cn("flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-medium transition-colors duration-200",
            isActive ? "bg-white/10 text-white shadow-[inset_3px_0_0_#D97706]" : "text-slate-400 hover:bg-white/5 hover:text-white")}>
          <Icon size={18} />{label}
        </NavLink>
      ))}
    </nav>
    <div className="flex items-center gap-3 rounded-2xl bg-white/5 p-3">
      <Avatar name={user.name} size="sm" className="bg-gold text-white" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-white">{user.name}</div>
        <div className="truncate text-xs text-slate-400">{user.email}</div>
      </div>
      <button data-testid="sidebar-logout-button" onClick={onLogout} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-white/10 hover:text-white" aria-label="Logout"><LogOut size={16} /></button>
    </div>
  </aside>
);

const BottomTabs = ({ items }) => (
  <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] md:hidden">
    <div className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
      {items.map(({ to, label, icon: Icon, id }) => (
        <NavLink key={id} to={to} data-testid={`mobile-nav-${id}-tab`}
          className={({ isActive }) => cn("flex min-h-[60px] flex-col items-center justify-center gap-1 text-[10.5px] font-semibold transition-colors", isActive ? "text-gold" : "text-slate-400")}>
          {({ isActive }) => (<><span className={cn("grid h-7 w-12 place-items-center rounded-full transition-colors", isActive && "bg-amber-50")}><Icon size={19} /></span>{label}</>)}
        </NavLink>
      ))}
    </div>
  </nav>
);

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV[user.user_type];
  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-page">
      <Sidebar items={items} user={user} onLogout={onLogout} />
      <div className="md:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 sm:px-6 md:px-10">
          <div className="md:hidden"><Brand /></div>
          <div className="hidden md:block text-sm text-slate-500">Welcome back, <span className="font-semibold text-navy">{user.name}</span></div>
          <div className="flex items-center gap-2">
            <RoleBadge type={user.user_type} />
            <button data-testid="topbar-logout-button" onClick={onLogout} className="grid h-11 w-11 place-items-center rounded-xl text-slate-500 transition-colors hover:bg-slate-100 hover:text-navy" aria-label="Logout"><LogOut size={18} /></button>
          </div>
        </header>
        <main className="mx-auto max-w-6xl px-4 pb-28 pt-6 sm:px-6 md:px-10 md:pb-12 md:pt-10 animate-fade-up">
          <Outlet />
        </main>
      </div>
      <BottomTabs items={items} />
    </div>
  );
}
